/**
 * Telemetry Service
 * 
 * Logs events to IndexedDB telemetry table (v5) when available
 */

import { useDatabase } from '../hooks/useDatabase';

/**
 * Log telemetry event
 */
export async function logTelemetry(
  eventType: string,
  metadata: Record<string, any>
): Promise<void> {
  try {
    // Check if we have access to database (not in worker context)
    if (typeof window === 'undefined') {
      console.log(`[Telemetry] ${eventType}:`, metadata);
      return;
    }

    // Log to console in development
    if (process.env.NODE_ENV === 'development') {
      console.log(`[Telemetry] ${eventType}:`, metadata);
    }

    // Try to log to IndexedDB v5 if available
    // This will be enhanced when v5 migration is enabled
    const db = (window as any).__dbAdapter;
    if (db && typeof db.logTelemetry === 'function') {
      await db.logTelemetry(eventType, metadata);
    }
  } catch (error) {
    console.error('Telemetry logging failed:', error);
  }
}

/**
 * Log worker event
 */
export function logWorkerEvent(
  workerType: 'encoder' | 'upload',
  event: string,
  data: Record<string, any>
): void {
  logTelemetry(`worker_${event}`, {
    workerType,
    ...data,
  });
}

/**
 * Log upload event
 */
export function logUploadEvent(
  event: string,
  videoGuid: string,
  data: Record<string, any> = {}
): void {
  logTelemetry(`upload_${event}`, {
    videoGuid,
    ...data,
  });
}

/**
 * Log error event
 */
export function logError(
  source: string,
  error: Error | string,
  context: Record<string, any> = {}
): void {
  logTelemetry('error', {
    source,
    error: typeof error === 'string' ? error : error.message,
    stack: typeof error === 'object' ? error.stack : undefined,
    ...context,
  });
}
